import classNames from 'volta/helpers/class-names';
import isPresent from 'volta/helpers/is-present';
import orElse from 'volta/helpers/or-else';
import stopPropagation from 'volta/helpers/stop-propagation';
import onClickOutside from '../../modifiers/on-click-outside';
import BvAnimated from 'volta/components/bv-animated';
import BvAvatarGroup from 'volta/components/bv-avatar-group';
import BvBtn from 'volta/components/bv-btn';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvCheckbox from 'volta/components/bv-checkbox';
import BvDateField from 'volta/components/bv-date-field';
import BvDropdownMenu from 'volta/components/bv-dropdown-menu';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvPopover from 'volta/components/bv-popover';
import BvRichText from 'volta/components/bv-rich-text';
import BvTag from 'volta/components/bv-tag';
import BvText from 'volta/components/bv-text';
import BvTextField from 'volta/components/bv-text-field';
import DomainUserSelect from '../domain-user-select';
import TaskTypeBadge from '../tasks-kit/task-type-badge';
import UserListItem from '../user/list-item';
import { concat, fn, array, hash, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import includes from '@nullvoxpopuli/ember-composable-helpers/helpers/includes';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import AnimatedIf from 'ember-animated/components/animated-if';
import { dropTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import t from 'ember-intl/helpers/t';
import momentFormat from 'ember-moment/helpers/moment-format';
import now from 'ember-moment/helpers/now';
import utc from 'ember-moment/helpers/utc';
import { set } from 'ember-set-helper';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import isEmpty from 'ember-truth-helpers/helpers/is-empty';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import _isNil from 'lodash/isNil';
import lowerCase from 'lodash/lowerCase';
import moment from 'moment-timezone';
import { formatISODate, formatLocalDate, formatRelativeDate } from 'volta/utils/date-utils';
import { enterMetaKeyLabel } from 'volta/utils/dom-utils';
import { inFilter, niFilter } from 'volta/utils/filters-utils';

import type { IntlService } from 'ember-intl';
import type { IResourceName } from 'volta/models/base-model';
import type { TTaskResolveIf, TTaskType, UTask } from 'volta/models/types/tasks';
import type User from 'volta/models/user';
import type UserProfile from 'volta/models/user-profile';
import type AuthorizationService from 'volta/services/authorization';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';

interface BvTaskSignature {
  Args: {
    task: UTask;
    resourceName?: IResourceName;
    isSelected?: boolean;
    disabled?: boolean;
    isResolvable?: boolean;
    isDue?: boolean;
    isAssignable?: boolean;
    taskType?: TTaskType;
    authorizedUsers?: string[];
    isAutoResolvable?: boolean;
    showTaskType?: boolean;
    onLightBg?: boolean;
    /* Actions */
    onSelect?: (task?: UTask, forceDelete?: boolean) => void;
    onSubmit?: (task: UTask) => Promise<void>;
    onResolve?: (task: UTask) => Promise<void>;
    onDelete?: (task: UTask) => Promise<void>;
  };
  Blocks: {
    actions: [];
    payload: [unknown, unknown];
  };
  Element: HTMLDivElement;
}

const ResolveIfIcons = {
  MANUAL: 'checkbox-outline',
  OUT_OF_BUFFER_ALERT: 'timer-outline'
};

export default class BvTaskComponent extends Component<BvTaskSignature> {
  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      id={{concat 'BvTask' (orElse @task.id 'New')}}
      class={{classNames
        'bv-task'
        (if @isSelected 'bv-task--is-selected')
        (if this.isOwn 'bv-task--is-own')
        (unless @task.id 'bv-task--is-new')
        (if @task.resolvedAt 'bv-task--is-resolved')
        (if @disabled 'bv-task--is-disabled')
        (if @onLightBg 'bv-task--on-light')
      }}
      ...attributes
      {{onClickOutside
        (fn this.handleClickOutside false)
        event='mouseup'
        exceptSelector='.bv-popover, .ember-power-select-dropdown'
      }}
    >

      <BvAnimated @contained={{true}} class='u-full-width' as |animated|>
        {{#AnimatedIf @isSelected use=animated.verticalList duration=200}}
          <div
            class='bv-task__display bv-task__display--selected'
            role={{if @onSelect 'button'}}
            {{on
              'click'
              (if (or @disabled @isSelected) (optional) (fn (optional @onSelect) @task false))
            }}
          >
            {{#if (or this.canDelete this.canUpdate)}}
              <header class={{classNames 'bv-task__header' (if this.readOnly 'u-pt-5')}}>
                <BvInline
                  class='bv-task__author-time'
                  @blockAlign='center'
                  @align='space-between'
                  @gap='2'
                >
                  <div>
                    {{#if @task.id}}
                      <BvText
                        @text={{unless
                          this.isOwn
                          (if
                            @task.resolvedBy
                            (concat (t 'tasksPage.resolvedBy') ' ' @task.resolvedBy.displayName)
                            (t
                              'tasksPage.createdBy'
                              name=(if
                                @task.createdBy
                                @task.createdBy.displayName
                                this.sessionUser.user.displayName
                              )
                            )
                          )
                        }}
                        data-test-resolved-by={{or
                          @task.resolvedBy.displayName
                          @task.createdBy.displayName
                        }}
                        @variant='body-sm'
                      />
                      <time
                        class='bv-task__created-at'
                        datetime='{{if @task.resolvedBy this.resolvedAtDisplay @task.createdAt}}'
                      >
                        <BvText @variant='body-sm' @color='subdued'>
                          <span data-test-resolve-date>
                            {{concat
                              (if
                                this.isOwn
                                (concat (t 'createdBy') ' ' (lowerCase (t 'you')) ' — ')
                                ' — '
                              )
                              (if @task.resolvedBy this.resolvedAtDisplay this.createdAtDisplay)
                            }}
                          </span>
                          <span data-test-resolve-time>
                            {{momentFormat
                              (utc (if @task.resolvedBy @task.resolvedAt @task.createdAt))
                              'LT'
                            }}
                          </span>
                        </BvText>
                      </time>
                    {{/if}}
                  </div>
                  {{#unless this.readOnly}}
                    <BvBtnGroup
                      @segmented={{false}}
                      @subtle={{true}}
                      @disabled={{@disabled}}
                      @size='sm'
                      class='u-justify-content-end'
                      as |g|
                    >
                      {{#if this.showResolvePopover}}
                        <BvDropdownMenu
                          class='bv-task__resolvable'
                          @size='sm'
                          @horizontalPosition='right'
                          @disabled={{@disabled}}
                          @sections={{array
                            (hash
                              title=(t 'tasksPage.resolveWhen')
                              items=(array
                                (hash
                                  content=(t 'manual')
                                  icon=this.ResolveIfIcons.MANUAL
                                  onClick=(fn this.changeResolution 'MANUAL')
                                  selected=(eq this.resolveIf 'MANUAL')
                                )
                                (if
                                  (includes @taskType (array 'PLANNING_TASK' 'EXECUTION_TASK'))
                                  (hash
                                    content=(t 'tasksPage.outOfBufferAlert')
                                    icon=this.ResolveIfIcons.OUT_OF_BUFFER_ALERT
                                    onClick=(fn this.changeResolution 'OUT_OF_BUFFER_ALERT')
                                    selected=(eq this.resolveIf 'OUT_OF_BUFFER_ALERT')
                                  )
                                )
                              )
                            )
                          }}
                        >
                          <:trigger as |popover|>
                            <g.btn
                              @iconLeft={{orElse
                                (get this.ResolveIfIcons this.resolveIf)
                                'checkbox-outline'
                              }}
                              @onClick={{popover.open}}
                              @testName='resolve-if'
                              {{popover.trigger}}
                            />
                          </:trigger>
                        </BvDropdownMenu>
                      {{/if}}
                      {{#if this.canDelete}}
                        <g.btn
                          @iconLeft='trash-outline'
                          @onClick={{perform this.handleDelete}}
                          @testName='delete-task'
                        />
                      {{/if}}
                    </BvBtnGroup>
                  {{/unless}}
                </BvInline>
              </header>
            {{/if}}
            <div class='bv-task__checkbox'>
              <BvCheckbox
                @onChange={{perform this.handleResolve}}
                @labelHidden={{true}}
                @disabled={{or
                  @disabled
                  (not @task.id)
                  (not (if @task.isResolved this.canReopen this.canResolve))
                }}
                @checked={{@task.isResolved}}
                @testName='check-resolve'
              />
            </div>
            <main class='bv-task__body u-mb-2'>
              <BvTextField
                @name='task-comment'
                @multiline={{true}}
                @value={{@task.comment}}
                @onChange={{set @task 'comment'}}
                @onEnter={{perform this.handleBodyEnter}}
                @focused={{true}}
                @placeholder={{t 'tasksPage.defaultTask'}}
                @plain={{true}}
                @readOnly={{isPresent @task.resolvedAt}}
                @onEscape={{fn this.handleClickOutside true}}
                @disabled={{or this.readOnly (not this.canUpdate)}}
                class='bv-task__comment-input u-full-width'
              />
              {{yield @task @isSelected to='payload'}}
            </main>
            <footer class='bv-task__footer'>
              <BvInline @gap='3' @blockAlign='center' @align='space-between' class='u-full-width'>
                <BvInline @gap='1' @blockAlign='center'>
                  <BvPopover
                    @disabled={{@disabled}}
                    class='bv-task__resolvable'
                    {{! @glint-expect-error: Wrong type for function }}
                    @onClose={{if @task.id (fn @onSubmit @task)}}
                    @size='sm'
                    as |popover|
                  >
                    {{#if this.assignableUsers.length}}
                      <BvBtn
                        @disabled={{this.readOnly}}
                        @subtle={{true}}
                        @onClick={{popover.toggle}}
                        @size='sm'
                        @testName='add-assignees'
                        class='u-px-1'
                        {{popover.trigger}}
                      >
                        <BvAvatarGroup
                          @users={{this.assignableUsers}}
                          @size='xs'
                          class='bv-task__assignees'
                        />
                      </BvBtn>
                    {{else}}
                      <BvBtn
                        @disabled={{this.readOnly}}
                        @subtle={{true}}
                        @size='sm'
                        @iconLeft='person-add-outline'
                        @onClick={{popover.toggle}}
                        @testName='add-assignees'
                        {{popover.trigger}}
                      />
                    {{/if}}
                    <popover.Content class='u-p-2'>
                      <DomainUserSelect
                        @label={{t 'tasksPage.assign'}}
                        @values={{@task.assigneeIds}}
                        @beforeQuery={{this.decorateUserQuery}}
                        @multiple={{true}}
                        @fill={{true}}
                        {{! @glint-expect-error: Wrong type for function }}
                        @onChange={{this.changeAssignees}}
                        @selectedItemComponent={{component UserListItem isSelected=true}}
                        as |user|
                      >
                        <UserListItem @option={{user}} />
                      </DomainUserSelect>
                    </popover.Content>
                  </BvPopover>
                  {{#if @isDue}}
                    <div class='bv-task__due-date'>
                      {{#let
                        (or this.readOnly (not this.canUpdate) (not this.isOwn))
                        as |dueDisabled|
                      }}
                        <BvDateField
                          @size='sm'
                          @value={{@task.dueDateAsDate}}
                          @disabled={{dueDisabled}}
                          {{! @glint-expect-error: Wrong type for function }}
                          @onChange={{this.changeDueDate}}
                          @minDate={{(now)}}
                          @horizontalPosition='right'
                          @testName='add-due-date'
                        >
                          <:trigger>
                            {{#if @task.dueDateAsDate}}
                              <BvTag
                                @onRemove={{unless dueDisabled (fn this.changeDueDate undefined)}}
                                @color={{@task.todoCriticality}}
                                @size='sm'
                                @text={{this.formattedDueDate}}
                                @disabled={{@disabled}}
                                class='u-pl-1'
                              />
                            {{else}}
                              <BvIcon @glyph='flag-outline' @tooltip={{t 'dueDate'}} @size='sm' />
                            {{/if}}
                          </:trigger>
                        </BvDateField>
                      {{/let}}
                    </div>
                  {{/if}}
                </BvInline>
                <BvBtn
                  @size='sm'
                  @color='primary'
                  @iconLeft='send'
                  @onClick={{stopPropagation (perform this.handleSubmitBtnClick)}}
                  @tooltip={{array (hash text=(t 'save') subtext=this.enterMetaKeyLabel)}}
                  @disabled={{or
                    this.readOnly
                    (isEmpty @task.comment)
                    (not @task.hasDirtyAttributes)
                  }}
                />
              </BvInline>
            </footer>
          </div>
        {{else}}
          <div class='bv-task__display'>
            <BvCheckbox
              @onChange={{perform this.handleResolve}}
              @labelHidden={{true}}
              @disabled={{or @disabled (not (if @task.isResolved this.canReopen this.canResolve))}}
              @checked={{@task.isResolved}}
              @testName='check-resolve'
              class='u-mr-4'
            />
            <div
              class='bv-task__display'
              role={{if @onSelect 'button'}}
              {{on 'click' (if @isSelected (optional) (fn (optional @onSelect) @task))}}
            >
              <div class='bv-task__collapsed-comment'>
                <BvRichText
                  @text={{if (isEmpty @task.comment) (t 'tasksPage.defaultTask') @task.comment}}
                  data-test-comment
                />
              </div>
              <BvInline @gap='1' @blockAlign='center'>
                {{#if (has-block 'actions')}}
                  {{yield to='actions'}}
                {{/if}}
                {{#if (and @showTaskType this.taskType)}}
                  <TaskTypeBadge @taskType={{this.taskType}} />
                {{/if}}
                {{#if @task.assignees}}
                  <BvAvatarGroup
                    @users={{@task.assignees}}
                    @size='xs'
                    class='bv-task__collapsed-assignees'
                  />
                {{/if}}
                {{#if (and @task.dueDate (not @task.isResolved))}}
                  <div
                    class='bv-task__collapsed-due-date u-d-inline-flex
                      {{concat "u-color-" @task.todoCriticality}}'
                  >
                    <BvIcon
                      @glyph='flag'
                      @size='sm'
                      class='u-mr-1'
                      @color={{@task.todoCriticality}}
                    />
                    <BvText
                      @text={{this.formattedDueDate}}
                      @variant='body-sm'
                      @color='subdued'
                      data-test-due-date
                    />
                  </div>
                {{/if}}
              </BvInline>
            </div>
          </div>
        {{/AnimatedIf}}
      </BvAnimated>
    </div>
  </template>
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;
  @service store!: StoreService;
  @service authorization!: AuthorizationService;

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked assignableUsers: (User | UserProfile)[] = this.args.task.assignees;

  ResolveIfIcons = ResolveIfIcons;
  enterMetaKeyLabel = enterMetaKeyLabel;

  get taskType() {
    return this.args.task.taskType ?? this.args.taskType;
  }

  get resolveIf() {
    return this.args.task.resolveIf ?? 'MANUAL';
  }

  get readOnly() {
    return (
      this.args.disabled === true ||
      (!this.showResolvePopover && !this.canDelete) ||
      _isNil(this.args.resourceName)
    );
  }

  get canUpdate() {
    return this.canCmd('UpdateTask');
  }

  get canDelete() {
    return this.canCmd('DeleteTask');
  }

  get canResolve() {
    return this.canCmd('ResolveTask');
  }

  get canReopen() {
    return this.canCmd('ReopenTask');
  }

  get isOwn() {
    return this.createdBy?.id === this.sessionUser.userId;
  }

  get createdBy() {
    return this.args.task.createdBy ?? this.sessionUser.user;
  }

  get createdAtDisplay() {
    const { createdAt } = this.args.task;
    return this.displayTimeAt(createdAt);
  }

  get resolvedAtDisplay() {
    const { resolvedAt } = this.args.task;
    return this.displayTimeAt(resolvedAt);
  }

  get formattedDueDate(): string | undefined {
    const dueDate = this.args.task.dueDateAsDate;
    return formatRelativeDate(dueDate, this.intl.t('today'), this.intl.t('tomorrow'));
  }

  get showResolvePopover() {
    return (
      this.args.isAutoResolvable === true && _isNil(this.args.task?.resolvedAt) && this.canUpdate
    );
  }

  handleBodyEnter = dropTask(async (_value: string, _id: string, event: KeyboardEvent) => {
    event.preventDefault();
    return this.args.onSubmit?.(this.args.task);
  });

  handleSubmitBtnClick = dropTask(async (event: MouseEvent) => {
    event.preventDefault();
    return await this.args.onSubmit?.(this.args.task);
  });

  handleResolve = dropTask(async (checked: boolean, e: InputEvent) => {
    e.stopPropagation();
    await timeout(checked ? 1000 : 500);
    this.args.task.resolvedAt = checked ? new Date() : null;
    return await this.args.onResolve?.(this.args.task);
  });

  handleDelete = dropTask(async () => {
    return await this.args.onDelete?.(this.args.task);
  });

  @action
  decorateUserQuery(query: IJsonApiQuery) {
    const { filter = {} } = query;
    filter['userType'] = niFilter(['API', 'SUPPLIER']);

    if (this.args.authorizedUsers?.length) {
      filter['id'] = inFilter(this.args.authorizedUsers ?? []);
    }

    query.filter = filter;
    return query;
  }

  @action
  changeAssignees(assigneeIds: string[], assignees: User[]) {
    this.args.task.assigneeIds = assigneeIds;
    this.assignableUsers = assignees;
  }

  @action
  changeResolution(resolveIf: TTaskResolveIf | undefined) {
    this.args.task.resolveIf = resolveIf;
    return this.args.onSubmit?.(this.args.task);
  }

  @action
  changeDueDate(dueDate: Date | undefined) {
    this.args.task.dueDate = dueDate ? formatISODate(dueDate) : null;
  }

  @action
  handleEscape(_event: KeyboardEvent) {
    this.handleClickOutside(true);
  }

  @action
  handleClickOutside(forceDelete: boolean = false) {
    if (this.args.isSelected) {
      this.args.onSelect?.(undefined, forceDelete);
    }
  }

  private canCmd(cmd: string) {
    return this.args.resourceName
      ? this.authorization.canStrict(this.args.resourceName.plural, cmd)
      : false;
  }

  private displayTimeAt(time?: Date | null): string | undefined {
    if (!time) {
      return undefined;
    }
    const mTime = moment.utc(time);
    const isToday = mTime.isSame(moment.utc(), 'day');
    const isYesterday = mTime.isSame(moment.utc().subtract(1, 'day'));
    return isToday || isYesterday
      ? this.intl.t(isToday ? 'today' : 'yesterday')
      : formatLocalDate(mTime);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvTask: typeof BvTaskComponent;
    'bv-task': typeof BvTaskComponent;
  }
}
